import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatures } from '../contexts/FeaturesContext';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import { alpha, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import NotificationBell from './common/NotificationBell';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { APP_SHELL_BAR_STYLES } from '../theme/appShell';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const menuPaperSx = {
  mt: 1,
  minWidth: 220,
  borderRadius: 2,
  boxShadow: '0 10px 40px rgba(0,0,0,0.18)',
  border: '1px solid',
  borderColor: 'divider',
  '& .MuiMenuItem-root': { borderRadius: 1, mx: 0.5, my: 0.25 },
};

const isPathActive = (pathname, link) => {
  if (!link) return false;
  if (pathname === link) return true;
  return pathname.startsWith(`${link}/`);
};

const branchIsActive = (item, pathname) => {
  if (!item.subMenu) {
    return isPathActive(pathname, item.link);
  }
  return item.subMenu.some((sub) =>
    sub.subMenu ? branchIsActive(sub, pathname) : isPathActive(pathname, sub.link)
  );
};

function MobileNavNode({ item, depth, pathname, onNavigate, onInitOpen }) {
  const [open, setOpen] = useState(() => (item.subMenu ? onInitOpen(item) : false));

  if (!item.subMenu) {
    return (
      <ListItemButton
        selected={isPathActive(pathname, item.link)}
        onClick={() => onNavigate(item.link)}
        sx={{ pl: 2 + depth * 1.5, py: 1.1 }}
      >
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: 500 }}
        />
      </ListItemButton>
    );
  }

  return (
    <Box>
      <ListItemButton onClick={() => setOpen((p) => !p)} sx={{ pl: 2 + depth * 1.5, py: 1.1 }}>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: 600 }}
        />
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {item.subMenu.map((sub) => (
            <MobileNavNode
              key={sub.label}
              item={sub}
              depth={depth + 1}
              pathname={pathname}
              onNavigate={onNavigate}
              onInitOpen={onInitOpen}
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

const NavBar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout: authLogout } = useAuth();
  const { contractsEnabled, loading: featuresLoading } = useFeatures();
  const prevPathRef = useRef(location.pathname);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElMenus, setAnchorElMenus] = useState({});
  const [brandingLogoUrl, setBrandingLogoUrl] = useState(null);
  const [companyLegalName, setCompanyLegalName] = useState(null);
  const [logoImageFailed, setLogoImageFailed] = useState(false);

  const loadBranding = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/settings/branding`);
      if (res.data?.success) {
        setBrandingLogoUrl(res.data.logo_url || null);
        setCompanyLegalName(res.data.company_legal_name || null);
        return;
      }
    } catch {
      /* try legacy */
    }
    try {
      const res = await axios.get(`${apiUrl}/settings/logo`);
      if (res.data?.logo_url) {
        setBrandingLogoUrl(res.data.logo_url);
      }
    } catch {
      /* keep prior / empty */
    }
  }, []);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  useEffect(() => {
    const prev = prevPathRef.current;
    if (prev === '/settings' && location.pathname !== '/settings') {
      loadBranding();
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname, loadBranding]);

  useEffect(() => {
    setLogoImageFailed(false);
  }, [brandingLogoUrl]);

  const handleOpenSubMenu = (event, key) => {
    setAnchorElMenus((prev) => ({ ...prev, [key]: event.currentTarget }));
  };

  const handleCloseSubMenu = (key) => {
    setAnchorElMenus((prev) => ({ ...prev, [key]: null }));
  };

  const closeAllMenus = useCallback(() => {
    setAnchorElMenus({});
  }, []);

  const handleLogout = () => {
    authLogout();
    closeAllMenus();
    setMobileOpen(false);
    navigate('/login');
  };

  const menuItems = useMemo(() => {
    const ordersSubMenu = [
      {
        label: 'Quotes',
        subMenu: [
          { label: 'All Quotes', link: '/quotes' },
          { label: 'Add Quote', link: '/quotes/new' },
        ],
      },
      {
        label: 'Orders',
        subMenu: [
          { label: 'All Orders', link: '/orders' },
          { label: 'Add Order', link: '/orders/new' },
        ],
      },
      {
        label: 'Invoices',
        subMenu: [
          { label: 'Add Invoice', link: '/invoice/new' },
          { label: 'All Invoices', link: '/invoice' },
        ],
      },
      { label: 'All Enquiries', link: '/enquiries' },
    ];
    if (contractsEnabled && !featuresLoading) {
      ordersSubMenu.push({ label: 'Contracts', link: '/contracts' });
    }
    return [
      { label: 'Dashboard', link: '/dashboard' },
      {
        label: 'Inventory',
        subMenu: [
          { label: 'Availability Calendar', link: '/inventory-calendar' },
          { label: 'All Inventory', link: '/inventory' },
          { label: 'Add Inventory', link: '/inventory/new' },
          { label: 'Categories', link: '/categories' },
        ],
      },
      {
        label: 'Orders Management',
        subMenu: ordersSubMenu,
      },
      {
        label: 'Customers',
        subMenu: [
          { label: 'All Customers', link: '/CustomerList' },
          { label: 'Add Customers', link: '/customer/new' },
        ],
      },
      {
        label: 'Accounts',
        subMenu: [
          { label: 'Chart of Accounts', link: '/accouonts' },
          { label: 'New Chart of Accounts', link: '/accouonts/new' },
          {
            label: 'Payments',
            subMenu: [
              { label: 'Add Payment', link: '/payment/new' },
              { label: 'All Payments', link: '/payment' },
            ],
          },
          {
            label: 'Expense',
            subMenu: [
              { label: 'Add Expense', link: '/expense/new' },
              { label: 'All Expense', link: '/expense' },
            ],
          },
          { label: 'Bank Connections', link: '/bank-connections' },
          { label: 'Reconciliation', link: '/reconciliation' },
        ],
      },
      {
        label: 'Reports',
        subMenu: [{ label: 'Profitability Report', link: '/reports/profit-report' }],
      },
      {
        label: 'Timesheets',
        subMenu: [
          { label: 'My Timesheets', link: '/timesheets' },
        ],
      },
      { label: 'Extensions', link: '/extensions' },
      { label: 'Conversations', link: '/conversations' },
      { label: 'Settings', link: '/settings' },
    ];
  }, [contractsEnabled, featuresLoading]);

  const buttonSx = (active) => ({
    color: 'inherit',
    my: 0.5,
    mx: 0.25,
    px: 1.25,
    py: 0.75,
    minHeight: 40,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
    borderRadius: 2,
    border: '1px solid',
    borderColor: active ? alpha('#fff', 0.2) : 'transparent',
    backgroundColor: active ? alpha('#fff', 0.1) : 'transparent',
    '&:hover': {
      backgroundColor: alpha('#fff', 0.12),
      borderColor: alpha('#fff', 0.12),
    },
  });

  const renderMenuItem = (item) => {
    if (!item.subMenu) {
      const active = isPathActive(location.pathname, item.link);
      return (
        <Button
          key={item.label}
          onClick={() => {
            navigate(item.link);
            closeAllMenus();
          }}
          sx={buttonSx(active)}
        >
          {item.label}
        </Button>
      );
    }

    const anchor = anchorElMenus[item.label] || null;
    const active = branchIsActive(item, location.pathname);

    return (
      <React.Fragment key={item.label}>
        <Button
          endIcon={
            <KeyboardArrowDownIcon
              sx={{ fontSize: 18, opacity: 0.85, transition: 'transform 0.2s', transform: anchor ? 'rotate(180deg)' : 'none' }}
            />
          }
          onClick={(e) => handleOpenSubMenu(e, item.label)}
          aria-expanded={Boolean(anchor)}
          aria-haspopup="true"
          sx={buttonSx(active)}
        >
          {item.label}
        </Button>
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => handleCloseSubMenu(item.label)}
          slotProps={{ paper: { elevation: 0, sx: menuPaperSx } }}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        >
          {item.subMenu.map((sub) =>
            sub.subMenu ? (
              <Box key={sub.label}>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenSubMenu(e, sub.label);
                  }}
                  sx={{ fontWeight: 600 }}
                >
                  {sub.label}
                  <Box component="span" sx={{ flex: 1 }} />
                  <KeyboardArrowDownIcon sx={{ fontSize: 18, transform: 'rotate(-90deg)', opacity: 0.6 }} />
                </MenuItem>
                <Menu
                  anchorEl={anchorElMenus[sub.label]}
                  open={Boolean(anchorElMenus[sub.label])}
                  onClose={() => handleCloseSubMenu(sub.label)}
                  slotProps={{ paper: { elevation: 0, sx: { ...menuPaperSx, minWidth: 200 } } }}
                  transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                >
                  {sub.subMenu.map((nested) => (
                    <MenuItem
                      key={nested.label}
                      onClick={() => {
                        navigate(nested.link);
                        handleCloseSubMenu(sub.label);
                        handleCloseSubMenu(item.label);
                        closeAllMenus();
                      }}
                    >
                      {nested.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            ) : (
              <MenuItem
                key={sub.label}
                onClick={() => {
                  navigate(sub.link);
                  handleCloseSubMenu(item.label);
                  closeAllMenus();
                }}
              >
                {sub.label}
              </MenuItem>
            )
          )}
        </Menu>
      </React.Fragment>
    );
  };

  const handleMobileNavigate = (link) => {
    navigate(link);
    setMobileOpen(false);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          ...APP_SHELL_BAR_STYLES,
          zIndex: theme.zIndex.appBar,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 56, md: 60 },
            px: { xs: 1.5, sm: 2, md: 3 },
            maxWidth: 1920,
            width: '100%',
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, md: 0 },
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              flex: { xs: '1 1 auto', md: '0 0 280px' },
            }}
          >
            <IconButton
              color="inherit"
              edge="start"
              aria-label="open menu"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { md: 'none' }, mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>

            <Box
              onClick={() => navigate('/dashboard')}
              role="button"
              tabIndex={0}
              aria-label="Go to dashboard"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/dashboard');
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                cursor: 'pointer',
                py: 0.5,
                px: 0.75,
                borderRadius: 2,
                outline: 'none',
                flexShrink: 0,
                minWidth: 0,
                '&:hover': { backgroundColor: alpha('#fff', 0.06) },
              }}
            >
              {brandingLogoUrl && !logoImageFailed ? (
                <Box
                  component="img"
                  src={brandingLogoUrl}
                  onError={() => setLogoImageFailed(true)}
                  alt=""
                  aria-hidden
                  sx={{
                    display: 'block',
                    maxHeight: 40,
                    maxWidth: { xs: 120, sm: 200 },
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Box
                  aria-hidden
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'linear-gradient(140deg, #3b82f6 0%, #6366f1 55%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '0.8rem',
                    color: '#fff',
                    boxShadow: '0 2px 12px rgba(99, 102, 241, 0.4)',
                    letterSpacing: '-0.02em',
                    flexShrink: 0,
                  }}
                >
                  IB
                </Box>
              )}
              <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 220,
                  }}
                >
                  {companyLegalName || 'Inventory'}
                </Typography>
                {!companyLegalName && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: alpha('#fff', 0.55), lineHeight: 1, display: 'block' }}
                  >
                    Booking
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 'auto', mr: 0.5 }}>
            {user && <NotificationBell />}
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: { xs: 'none', md: 'flex' },
              flexWrap: 'wrap',
              alignItems: 'center',
              alignContent: 'center',
              justifyContent: 'center',
              rowGap: 0.5,
              columnGap: 0.25,
            }}
          >
            {menuItems.map(renderMenuItem)}
          </Box>

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flex: { md: '0 0 200px' },
              justifyContent: 'flex-end',
            }}
          >
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationBell />
                <Button
                  variant="outlined"
                  onClick={handleLogout}
                  startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    color: alpha('#fff', 0.95),
                    borderColor: alpha('#f87171', 0.55),
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 1.5,
                    minHeight: 40,
                    flexShrink: 0,
                    '&:hover': {
                      borderColor: '#f87171',
                      backgroundColor: alpha('#f87171', 0.12),
                    },
                  }}
                >
                  Logout
                </Button>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        slotProps={{ paper: { sx: { width: 300, maxWidth: '88vw' } } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            background: (t) => alpha(t.palette.primary.main, 0.06),
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Menu
          </Typography>
          <IconButton size="small" onClick={() => setMobileOpen(false)} aria-label="close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <List component="nav" dense sx={{ py: 0.5 }}>
          {menuItems.map((item) => (
            <MobileNavNode
              key={item.label}
              item={item}
              depth={0}
              pathname={location.pathname}
              onNavigate={handleMobileNavigate}
              onInitOpen={(subtree) => branchIsActive(subtree, location.pathname)}
            />
          ))}
        </List>
        {user && (
          <>
            <Divider />
            <ListItemButton
              onClick={handleLogout}
              sx={{ py: 1.25, color: 'error.main' }}
            >
              <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
              <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItemButton>
          </>
        )}
      </Drawer>
    </>
  );
};

export default NavBar;
